'use client';

import { Email } from '@teamhanko/hanko-elements';
import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';

import { EnvVarManager } from '@/env/envVarManager';
import { useAppPush } from '@/hooks/useAppPush';
import { availableHankoLanguages } from '@/i18n/availableHankoLanguages';
import { IHanko } from '@/types/IHanko';
import { IHankoSession } from '@/types/IHankoSession';
import { IHankoUser, IHankoUserPlusError } from '@/types/IHankoUser';
import { IUserStatus } from '@/types/IUserStatus';
import { cloneAnything } from '@/utils/cloneAnything';
import { getFakeAuthenticated } from '@/utils/getFakeAuthenticated';

export interface IAuthContextProps {
  userStatus: IUserStatus;
  hankoSession: IHankoSession | undefined;
  hankoUser: IHankoUser | undefined;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<IAuthContextProps>({} as IAuthContextProps);

const hankoAPI: string = EnvVarManager.getEnvVar('NEXT_PUBLIC_HANKO_API_URL');
const fakeAuthenticated = getFakeAuthenticated();

export const AuthContextProvider = ({ children }: PropsWithChildren) => {
  const { push } = useAppPush();
  const [checkedHankoUser, setCheckedHankoUser] = useState<boolean>(false);
  const [hankoInstance, setHankoInstance] = useState<IHanko>();
  const [hankoUser, setHankoUser] = useState<IHankoUser>();
  const [hankoSession, setHankoSession] = useState<IHankoSession>();

  const hankoUserStatus: IUserStatus = useMemo((): IUserStatus => {
    if (!hankoInstance || !checkedHankoUser) {
      return 'CHECKING';
    } else if (!hankoUser || (hankoUser as IHankoUserPlusError)?.error) {
      return 'NO_USER';
    } else {
      return 'USER_VALID';
    }
  }, [checkedHankoUser, hankoInstance, hankoUser]);

  const userStatus: IUserStatus = fakeAuthenticated ? 'USER_VALID' : hankoUserStatus;

  const logout = useCallback(async () => {
    try {
      await hankoInstance?.logout();
      setHankoUser(undefined);
      return;
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }, [hankoInstance]);

  const logoutWhenExpired = useCallback(() => {
    if (hankoUser) {
      logout();
    }
  }, [hankoUser, logout]);

  const initHanko = useCallback(async () => {
    setCheckedHankoUser(false);
    const { register } = await import('@teamhanko/hanko-elements');
    register(hankoAPI, { translations: availableHankoLanguages })
      .then((value) => {
        const hanko = value.hanko;
        hanko.onSessionExpired(logoutWhenExpired);
        hanko.onSessionCreated(async () => {
          await updateUserAndSession(hanko);
          push('/'); // TODO #12345
        });
        setHankoInstance(hanko);
      })
      .catch((error) => {
        // handle error
        throw new Error('Registering hanko failed with error', error);
      });
  }, [logoutWhenExpired, push]);

  const fakeHanko = () => {
    setHankoInstance({} as IHanko);
    setHankoUser({ emails: ['fake-email@example.com' as unknown as Email], error: '' });
  };

  useEffect(() => {
    if (fakeAuthenticated) {
      fakeHanko();
    } else {
      initHanko();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateUserAndSession = async (hankoInstance: IHanko | undefined) => {
    setCheckedHankoUser(false);

    if (!hankoInstance || fakeAuthenticated) {
      return;
    }
    try {
      const user = await hankoInstance.getUser();
      const { emails } = user;
      const email = emails?.[0];
      if (!email) {
        throw new Error(`hanko getCurrent users has no email set`);
      }
      setHankoUser(user);
      setCheckedHankoUser(true);

      const { is_valid } = await hankoInstance.validateSession();
      const session = await hankoInstance.getSessionToken();

      if (!is_valid) {
        throw new Error('Session not valid');
      }

      setHankoSession({ token: session });
    } catch (error) {
      setHankoUser((prevState) => ({
        ...cloneAnything(prevState),
        error,
      }));
      setHankoSession((prevState) => ({
        ...cloneAnything(prevState),
        error: 'Invalid session',
      }));
      setCheckedHankoUser(true);
    }
  };

  useEffect(() => {
    updateUserAndSession(hankoInstance);
  }, [hankoInstance]);

  const value: IAuthContextProps = useMemo((): IAuthContextProps => {
    return {
      userStatus,
      hankoSession,
      hankoUser,
      logout,
    };
  }, [userStatus, hankoSession, hankoUser, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
